/**
 * Requirements profile — what a piece of work needs from whoever does it
 * (PRD Part A, Phases B and C).
 *
 * Three dimensions, all optional. Declare none and the qualified pool is the
 * whole pool: rough-cut planning is a supported mode forever, not a fallback.
 * Declare one and a below-the-line answer stops saying "no capacity" and starts
 * naming the gap a staffing conversation can act on.
 *
 * The planner never auto-assigns. Matching qualified supply to demand
 * determines FEASIBILITY; it never staffs a person onto work.
 */

import { useCallback, useEffect, useState } from 'react';
import { Award, Loader2, Plus, ShieldCheck, User, X } from 'lucide-react';
import {
  addPlanningRequirement,
  attachRequirementNames,
  getPlanningRequirements,
  listCredentials,
  removePlanningRequirement,
  type Credential,
} from '../../../lib/planningRequirementsService';
import { getPlanningResources } from '../../../lib/planningRosterService';
import { listCapabilities } from '../../../lib/capabilityService';
import type { PlanningResource } from '../../../lib/planningRoster';
import type { PlanningRequirement } from '../../../lib/planningEngineModule.types';
import type { Capability } from '../../../types/capabilities';

type Dimension = PlanningRequirement['dimension'];

const DIMENSION_ICON: Record<Dimension, typeof Award> = {
  capability: Award,
  credential: ShieldCheck,
  named_resource: User,
};

const DIMENSION_CHIP: Record<Dimension, string> = {
  capability: 'bg-primary-50 text-primary-700 border-primary-100',
  credential: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  named_resource: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
};

function describe(requirement: PlanningRequirement): string {
  switch (requirement.dimension) {
    case 'capability':
      return requirement.minLevel != null
        ? `${requirement.capabilityName ?? 'capability'} ≥ ${requirement.minLevel}`
        : (requirement.capabilityName ?? 'capability');
    case 'credential':
      return requirement.credentialName ?? 'credential';
    case 'named_resource':
      return `Only ${requirement.namedResourceName ?? 'a specific person'}`;
  }
}

export default function RequirementsPanel({
  workspaceId,
  priorityId,
  workUnitId,
}: {
  workspaceId: string;
  /** Exactly one of these is set — the grain this profile hangs off. */
  priorityId?: string;
  workUnitId?: string;
}) {
  const [requirements, setRequirements] = useState<PlanningRequirement[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [resources, setResources] = useState<PlanningResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<Dimension | null>(null);
  const [minLevel, setMinLevel] = useState(5);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [all, caps, creds, res] = await Promise.all([
        getPlanningRequirements(workspaceId),
        listCapabilities(workspaceId),
        listCredentials(workspaceId).catch(() => [] as Credential[]),
        getPlanningResources(workspaceId).catch(() => [] as PlanningResource[]),
      ]);
      const active = res.filter((r) => r.active);
      const mine = all.filter((r) =>
        priorityId ? r.priorityId === priorityId : r.workUnitId === workUnitId,
      );
      setRequirements(
        attachRequirementNames(mine, {
          capabilityNames: new Map((caps.data ?? []).map((c) => [c.id, c.name])),
          credentialNames: new Map(creds.map((c) => [c.id, c.name])),
          resourceNames: new Map(res.map((r) => [r.id, r.displayName])),
        }),
      );
      setCapabilities(caps.data ?? []);
      setCredentials(creds);
      setResources(active);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, priorityId, workUnitId]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async (payload: {
    capabilityId?: string;
    credentialId?: string;
    namedResourceId?: string;
    minLevel?: number | null;
  }) => {
    try {
      await addPlanningRequirement({
        workspaceId,
        priorityId: priorityId ?? null,
        workUnitId: workUnitId ?? null,
        ...payload,
      });
      setAdding(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const remove = async (id: string) => {
    setRequirements((prev) => prev.filter((r) => r.id !== id));
    try {
      await removePlanningRequirement(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await load();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[var(--text-ter)]">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading requirements…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
          Requires
        </h4>
        <div className="flex items-center gap-1">
          {(['capability', 'credential', 'named_resource'] as Dimension[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setAdding(adding === d ? null : d)}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium border transition-colors ${
                adding === d
                  ? DIMENSION_CHIP[d]
                  : 'border-dashed border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-ter)] hover:text-primary-600'
              }`}
            >
              <Plus className="h-2.5 w-2.5" />
              {d === 'named_resource' ? 'person' : d}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}

      {requirements.length === 0 && !adding && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Nothing declared — this plans against the whole pool. Add a requirement to make the
          engine answer with who, at what level.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {requirements.map((r) => {
          const Icon = DIMENSION_ICON[r.dimension];
          return (
            <span
              key={r.id}
              className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium border group ${DIMENSION_CHIP[r.dimension]}`}
            >
              <Icon className="h-3 w-3" />
              {describe(r)}
              <button
                type="button"
                onClick={() => void remove(r.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5 hover:text-[var(--coral)]"
                aria-label={`Remove requirement ${describe(r)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
      </div>

      {adding === 'capability' && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <select
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) void add({ capabilityId: e.target.value, minLevel });
            }}
            className="text-xs border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
          >
            <option value="">Which capability…</option>
            {capabilities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-xs text-[var(--text-ter)]">
            at level ≥
            <input
              type="number"
              min={1}
              max={10}
              value={minLevel}
              onChange={(e) => setMinLevel(Number(e.target.value))}
              className="w-12 text-xs border border-[var(--rule)] dark:border-[var(--rule)] rounded px-1 py-1 text-right bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
            />
          </label>
          {capabilities.length === 0 && (
            <span className="text-xs text-[var(--text-ter)]">No capabilities defined yet.</span>
          )}
        </div>
      )}

      {adding === 'credential' && (
        <div className="pt-1">
          <select
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) void add({ credentialId: e.target.value });
            }}
            className="text-xs border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
          >
            <option value="">Which credential…</option>
            {credentials.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {credentials.length === 0 && (
            <span className="ml-2 text-xs text-[var(--text-ter)]">
              No credentials defined yet — add them in People &amp; Capacity.
            </span>
          )}
        </div>
      )}

      {adding === 'named_resource' && (
        <div className="pt-1">
          <select
            defaultValue=""
            onChange={(e) => {
              if (e.target.value) void add({ namedResourceId: e.target.value });
            }}
            className="text-xs border border-[var(--rule)] dark:border-[var(--rule)] rounded px-2 py-1 bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
          >
            <option value="">Only this person…</option>
            {resources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.displayName}
              </option>
            ))}
          </select>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            The truly non-fungible case. Declaring it does not staff anyone — it only means nobody
            else's hours can cover this.
          </p>
        </div>
      )}
    </div>
  );
}
